import {ChangeDetectionStrategy, Component} from '@angular/core';
import {
  CartItem,
  CartItemInterface,
  SpecificationMap,
  Variant,
  YsoProduct,
} from '../../data/model';
import {select, Store} from '@ngrx/store';
import * as addonState from '../reducers';
import {CommerceState} from '../reducers';
import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {filterNullOrUndefined} from '../../common/service';
import {
  selectContextData,
  selectOrganizationName,
} from '../../common/context-data/context-data.selectors';
import * as eventActions from '../../common/event-emitter/event.actions';
import * as addonActions from '../add-on.actions';
import * as cartActions from '../../common/shopping-cart/cart.actions';

@Component({
  selector: 'app-yso-products',
  templateUrl: './yso-products-page.component.html',
  styleUrls: ['./yso-products-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-yso-products'},
  standalone: false,
})
export class YsoProductsPageComponent {
  products$: Observable<YsoProduct[]>;
  organizationName$: Observable<string | undefined>;
  specificationMap$: Observable<SpecificationMap>;
  variants$: Observable<Record<string, Variant[]>>;
  isMotionMode$: Observable<boolean>;

  constructor(private store: Store<CommerceState>) {
    this.products$ = this.store.pipe(
      select(addonState.selectAddonByCatalogId),
      filterNullOrUndefined(),
      map(addon => <YsoProduct[]>addon!.data),
    );
    this.specificationMap$ = this.store.pipe(
      select(addonState.selectSpecs),
      filterNullOrUndefined(),
      map(specs => specs!),
    );
    this.variants$ = this.store.pipe(
      select(addonState.selectVariants),
      filterNullOrUndefined(),
      map(v => v!),
    );
    this.organizationName$ = this.store.pipe(select(selectOrganizationName));
    this.isMotionMode$ = this.store.pipe(
      select(selectContextData),
      map(contextData => contextData?.signups[0]?.program?.system === 'Motion'),
    );
    this.products$.subscribe(products => {
      if (products.length === 1) {
        const product = products[0];
        // For single products, eagerly fetch specs/variants if not already present
        if (product.type !== 'Bundle') {
          const hasData =
            (product.variants?.length ?? 0) > 0 ||
            (product.specifications?.length ?? 0) > 0;

          if (!hasData) {
            this.getSpecifications(product.id);
          }
        }
      }
    });
  }

  addToCart(item: CartItem): void {
    this.store.dispatch(
      cartActions.addOrUpdateCartItem({
        item,
        options: {sync: true},
      }),
    );
  }

  addClareBundle(event: {
    items: CartItemInterface[];
    bundleDisplayName: string;
  }): void {
    this.store.dispatch(cartActions.addClareBundleToCart(event));
  }

  getSpecifications(productId: string): void {
    this.store.dispatch(addonActions.loadSpecs({productId}));
    this.store.dispatch(addonActions.loadVariants({productId}));
  }

  registerAnother(): void {
    this.isMotionMode$.pipe(take(1)).subscribe(isMotionMode => {
      if (!isMotionMode) {
        this.store.dispatch(eventActions.registerAnother());
      }
    });
  }

  complete(): void {
    this.isMotionMode$.pipe(take(1)).subscribe(isMotionMode => {
      if (!isMotionMode) {
        this.store.dispatch(cartActions.completeOrder());
      }
    });
  }

  valid(): boolean {
    return true;
  }
}
